// ============================================================================
// AUTH.FIREBASE.JS - Firebase Authentication
// 
// Handles email/password auth + user profile management.
// Profile creation is done via Cloud Function (initializeUserProfile).
// Client waits for profile to exist with retry/backoff.
// ============================================================================

import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import {
  getFirestore,
  doc,
  getDoc,
} from 'firebase/firestore';
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from 'firebase/functions';
import { initializeAppCheckForApp } from './appCheck.js';

// ============================================================================
// FIREBASE INITIALIZATION
// ============================================================================

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

// Initialize Firebase (singleton)
let app = null;
let auth = null;
let db = null;
let functions = null;

const initFirebase = () => {
  if (!app && firebaseConfig.apiKey) {
    app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
    // App Check (S2): attest this app instance before any sensitive callable
    // runs. No-op unless VITE_RECAPTCHA_SITE_KEY is set; idempotent across the
    // auth.firebase.js / store.firebase.js bootstrap paths.
    initializeAppCheckForApp(app);
    auth = getAuth(app);
    db = getFirestore(app);
    functions = getFunctions(app);
    
    // Connect to emulator in development
    if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
      connectFunctionsEmulator(functions, 'localhost', 5001);
    }
  }
  return { app, auth, db, functions };
};

export const getFirebaseAuth = () => {
  if (!auth) initFirebase();
  return auth;
};

export const getFirebaseDb = () => {
  if (!db) initFirebase();
  return db;
};

export const getFirebaseFunctions = () => {
  if (!functions) initFirebase();
  return functions;
};

// ============================================================================
// USER PROFILE MANAGEMENT
// ============================================================================

/**
 * Get user profile from Firestore
 */
export const getUserProfile = async (uid) => {
  const db = getFirebaseDb();
  const userRef = doc(db, 'users', uid);
  const userSnap = await getDoc(userRef);
  
  if (userSnap.exists()) {
    return { id: uid, ...userSnap.data() };
  }
  return null;
};

/**
 * Wait for user profile to exist with retry/backoff
 * Cloud Function creates the profile, client waits for it.
 * 
 * @param {string} uid - User ID
 * @param {number} maxAttempts - Maximum retry attempts (default 10)
 * @param {number} initialDelayMs - Initial delay in ms (default 500)
 * @returns {Promise<object>} - User profile
 * @throws {Error} - If profile not found after max attempts
 */
export const waitForUserProfile = async (uid, maxAttempts = 10, initialDelayMs = 500) => {
  let delay = initialDelayMs;
  
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    const profile = await getUserProfile(uid);
    
    if (profile) {
      return profile;
    }
    
    // Wait before next attempt with exponential backoff
    if (attempt < maxAttempts) {
      console.log(`Profile not found, attempt ${attempt}/${maxAttempts}. Retrying in ${delay}ms...`);
      await new Promise(resolve => setTimeout(resolve, delay));
      delay = Math.min(delay * 1.5, 3000); // Cap at 3 seconds
    }
  }
  
  throw new Error('User profile not found after maximum retry attempts');
};

/**
 * Initialize user profile via Cloud Function
 * Called after successful Auth signup.
 * 
 * @param {string} displayName - Optional display name
 * @returns {Promise<object>} - Created profile
 */
export const initializeProfile = async (displayName = null) => {
  const functions = getFirebaseFunctions();
  const initializeUserProfile = httpsCallable(functions, 'initializeUserProfile');
  
  try {
    const result = await initializeUserProfile({ displayName });
    return result.data;
  } catch (error) {
    console.error('Failed to initialize profile:', error);
    throw error;
  }
};

/**
 * Get or wait for user profile
 * First tries to get existing profile, then waits if not found.
 */
export const getOrWaitForProfile = async (user) => {
  // First try to get existing profile
  let profile = await getUserProfile(user.uid);
  
  if (profile) {
    return profile;
  }
  
  // Profile doesn't exist yet, might be a race condition
  // Wait for it with retry
  try {
    profile = await waitForUserProfile(user.uid, 8, 300);
    return profile;
  } catch (error) {
    console.error('Profile not found after waiting:', error);
    throw error;
  }
};

// ============================================================================
// AUTHENTICATION FUNCTIONS
// ============================================================================

/**
 * Sign up with email/password
 * 
 * Flow:
 * 1. Create Firebase Auth user
 * 2. Call Cloud Function to create Firestore profile
 * 3. Return user + profile
 */
export const signUp = async (email, password, displayName = null) => {
  const auth = getFirebaseAuth();
  
  try {
    // 1. Create Auth user
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    
    // 2. Update display name if provided
    if (displayName) {
      await updateProfile(credential.user, { displayName });
    }
    
    // 3. Call Cloud Function to create profile
    // This bypasses Firestore rules (Admin SDK)
    try {
      const result = await initializeProfile(displayName || email.split('@')[0]);
      
      return {
        success: true,
        user: credential.user,
        profile: result.profile,
        isFirstUser: result.isFirstUser || false,
      };
    } catch (cfError) {
      // Cloud Function failed, but Auth user was created
      // Try to wait for profile (might have been created before error)
      console.warn('Cloud Function error, waiting for profile...', cfError);
      
      try {
        const profile = await waitForUserProfile(credential.user.uid, 5, 500);
        return {
          success: true,
          user: credential.user,
          profile,
        };
      } catch (waitError) {
        // Profile truly doesn't exist, this is a problem
        console.error('Profile creation failed:', waitError);
        return {
          success: false,
          error: 'Account created but profile setup failed. Please try signing in.',
          user: credential.user,
        };
      }
    }
  } catch (error) {
    console.error('Sign up error:', error);
    return {
      success: false,
      error: getAuthErrorMessage(error.code || error.message),
    };
  }
};

/**
 * Sign in with email/password
 * 
 * Flow:
 * 1. Sign in with Firebase Auth
 * 2. Wait for profile to exist (may need Cloud Function for legacy users)
 * 3. Return user + profile
 */
export const signIn = async (email, password) => {
  const auth = getFirebaseAuth();
  
  try {
    // 1. Sign in
    const credential = await signInWithEmailAndPassword(auth, email, password);
    
    // 2. Get or wait for profile
    try {
      const profile = await getOrWaitForProfile(credential.user);
      
      return {
        success: true,
        user: credential.user,
        profile,
      };
    } catch (profileError) {
      // Profile doesn't exist, try to create via Cloud Function
      console.warn('Profile not found, attempting to create...', profileError);
      
      try {
        const result = await initializeProfile(credential.user.displayName || email.split('@')[0]);
        
        return {
          success: true,
          user: credential.user,
          profile: result.profile,
        };
      } catch (cfError) {
        console.error('Failed to create profile on sign in:', cfError);
        return {
          success: false,
          error: 'Profile not found. Please contact administrator.',
        };
      }
    }
  } catch (error) {
    console.error('Sign in error:', error);
    return {
      success: false,
      error: getAuthErrorMessage(error.code || error.message),
    };
  }
};

/**
 * Sign out
 */
export const logOut = async () => {
  const auth = getFirebaseAuth();
  
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Sign out error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Subscribe to auth state changes
 * 
 * When user signs in, waits for profile to be available.
 * Shows "Setting up account..." state while waiting.
 */
export const onAuthChange = (callback) => {
  const auth = getFirebaseAuth();
  
  // Track if we're currently processing a user
  let processing = false;
  
  return onAuthStateChanged(auth, async (user) => {
    if (user) {
      // Prevent duplicate processing
      if (processing) return;
      processing = true;
      
      try {
        // User is signed in, get or wait for profile
        const profile = await getOrWaitForProfile(user);
        callback({ user, profile, loading: false });
      } catch (error) {
        console.error('Failed to get profile on auth change:', error);
        
        // Try to create profile via Cloud Function
        try {
          const result = await initializeProfile(user.displayName || user.email?.split('@')[0]);
          callback({ user, profile: result.profile, loading: false });
        } catch (cfError) {
          console.error('Failed to create profile:', cfError);
          callback({ user, profile: null, loading: false, error: 'Profile setup failed' });
        }
      } finally {
        processing = false;
      }
    } else {
      // User is signed out
      callback({ user: null, profile: null, loading: false });
    }
  });
};

/**
 * Get current user
 */
export const getCurrentUser = () => {
  const auth = getFirebaseAuth();
  return auth?.currentUser || null;
};

// ============================================================================
// ERROR MESSAGES
// ============================================================================

const getAuthErrorMessage = (code) => {
  const messages = {
    'auth/email-already-in-use': 'This email is already registered.',
    'auth/invalid-email': 'Invalid email address.',
    'auth/operation-not-allowed': 'Email/password sign-in is not enabled.',
    'auth/weak-password': 'Password must be at least 6 characters.',
    'auth/user-disabled': 'This account has been disabled.',
    'auth/user-not-found': 'No account found with this email.',
    'auth/wrong-password': 'Incorrect password.',
    'auth/invalid-credential': 'Invalid email or password.',
    'auth/too-many-requests': 'Too many attempts. Please try again later.',
  };
  
  return messages[code] || code || 'An error occurred. Please try again.';
};

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  signUp,
  signIn,
  logOut,
  onAuthChange,
  getCurrentUser,
  getUserProfile,
  waitForUserProfile,
  initializeProfile,
  getOrWaitForProfile,
  getFirebaseAuth,
  getFirebaseDb,
  getFirebaseFunctions,
};
